'use client';

import React, { useEffect, useRef, useState } from 'react';

const BRAND = '#3757FF';

const sessions = [
    { subject: 'Algebra Fundamentals', tutor: 'Mr. Osei', time: 'Today · 3:00 PM', students: 24, status: 'live' },
    { subject: 'Essay Structure & Argumentation', tutor: 'Ms. Asante', time: 'Today · 5:30 PM', students: 18, status: 'upcoming' },
    { subject: 'Chemical Bonding', tutor: 'Dr. Frimpong', time: 'Tomorrow · 10:00 AM', students: 31, status: 'upcoming' },
    { subject: 'World History: Colonial Era', tutor: 'Mr. Darko', time: 'Tomorrow · 2:00 PM', students: 15, status: 'upcoming' },
];

const features = [
    {
        title: 'Real-time interaction',
        desc: 'Ask questions, respond to polls, and engage directly with your instructor during every session.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5h14M3 10h10M3 15h7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <circle cx="16" cy="15" r="3" stroke="currentColor" strokeWidth="1.5" />
                <path d="M16 14v1.5l1 1" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Session recordings',
        desc: 'Every live class is recorded and available for review — revisit any session at your own pace.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.75" />
                <path d="M8 8l5 2.5L8 13V8z" fill="currentColor" />
            </svg>
        ),
    },
    {
        title: 'Scheduled reminders',
        desc: 'Never miss a class. Get notified before sessions start so you can always show up prepared.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 2a6 6 0 016 6c0 3.5 1.5 5 1.5 5h-15S4 11.5 4 8a6 6 0 016-6z" stroke="currentColor" strokeWidth="1.75" />
                <path d="M8.5 17a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
        ),
    },
];

export default function LearnLiveClasses() {
    const sectionRef = useRef<HTMLElement>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    return (
        <section id="live-classes" ref={sectionRef} className="py-28 px-6 md:px-10 relative overflow-hidden" style={{ background: '#FAFAFA' }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 90% 30%, rgba(55,87,255,0.05) 0%, transparent 60%)' }} />

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-start">
                    {/* Left: content */}
                    <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-700 uppercase tracking-widest mb-6"
                            style={{ background: 'rgba(55,87,255,0.08)', color: BRAND, border: '1px solid rgba(55,87,255,0.15)', letterSpacing: '0.15em' }}
                        >
                            Live Classes
                        </div>
                        <h2
                            className="font-display font-800 mb-5"
                            style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.025em', color: '#0D0D0D' }}
                        >
                            Learn in real time,
                            <br />
                            <span style={{ color: BRAND }}>with real instructors.</span>
                        </h2>
                        <p className="text-base leading-relaxed mb-10" style={{ color: '#666', maxWidth: '460px' }}>
                            Join scheduled live sessions with qualified tutors. Ask questions, participate in discussions, and get the kind of engagement that recorded content alone can't provide.
                        </p>

                        {/* Feature list */}
                        <div className="flex flex-col gap-5">
                            {features.map((f) => (
                                <div key={f.title} className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>
                                        {f.icon}
                                    </div>
                                    <div>
                                        <p className="text-sm font-700 mb-1" style={{ color: '#0D0D0D' }}>{f.title}</p>
                                        <p className="text-sm leading-relaxed" style={{ color: '#666' }}>{f.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right: session list */}
                    <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <div className="rounded-3xl p-6" style={{ background: 'white', boxShadow: '0 16px 60px rgba(55,87,255,0.1)', border: '1px solid rgba(55,87,255,0.08)' }}>
                            <div className="flex items-center justify-between mb-6">
                                <p className="text-sm font-800" style={{ color: '#0D0D0D' }}>Upcoming Sessions</p>
                                <span className="text-xs font-600 px-3 py-1.5 rounded-full" style={{ background: 'rgba(55,87,255,0.08)', color: BRAND }}>4 this week</span>
                            </div>

                            <div className="flex flex-col gap-3">
                                {sessions.map((session, i) => (
                                    <div
                                        key={session.subject}
                                        className="rounded-2xl p-4 flex items-center gap-4 transition-all duration-200 cursor-pointer"
                                        style={{
                                            background: session.status === 'live' ? BRAND : '#F7F8FF',
                                            border: session.status === 'live' ? 'none' : '1px solid rgba(55,87,255,0.08)',
                                        }}
                                        onMouseEnter={e => { if (session.status !== 'live') { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.25)'; (e.currentTarget as HTMLElement).style.transform = 'translateX(4px)'; } }}
                                        onMouseLeave={e => { if (session.status !== 'live') { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)'; (e.currentTarget as HTMLElement).style.transform = 'translateX(0)'; } }}
                                    >
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ background: session.status === 'live' ? 'rgba(255,255,255,0.2)' : 'rgba(55,87,255,0.1)' }}
                                        >
                                            {session.status === 'live' ? (
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="3" fill="white" /><circle cx="7" cy="7" r="5.5" stroke="white" strokeWidth="1.25" opacity="0.5" /></svg>
                                            ) : (
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="2.5" width="11" height="9" rx="1.5" stroke={BRAND} strokeWidth="1.25" /><path d="M5.5 5.5l3 2-3 2V5.5z" fill={BRAND} /></svg>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-700 truncate" style={{ color: session.status === 'live' ? 'white' : '#0D0D0D' }}>{session.subject}</p>
                                            <p className="text-xs font-500" style={{ color: session.status === 'live' ? 'rgba(255,255,255,0.7)' : '#888' }}>{session.tutor} · {session.time}</p>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className="text-xs font-600" style={{ color: session.status === 'live' ? 'rgba(255,255,255,0.8)' : '#888' }}>{session.students}</span>
                                            {session.status === 'live' && (
                                                <span className="text-xs font-700 px-2.5 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>Join</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
